import { useState, useEffect } from "react";
import { ShieldCheck, Printer, Trash2, LogOut, Lock, ChevronDown } from "lucide-react";

// Interactive, print-only safety & exit plan. Nothing is stored — not on our
// servers, not in localStorage. State lives in memory and dies with the tab.
// Structure culled from the National DV Hotline "Create your personal safety plan"
// (thehotline.org) + womenshealth.gov leaving-an-abusive-relationship checklist.

const GOBAG = [
  "Driver's license / photo ID", "Birth certificates (yours & children's)",
  "Social Security cards", "Passports / immigration & green-card papers",
  "Cash, cards & a checkbook", "Recent bank & pay statements",
  "Protective order (a copy on you)", "Lease / mortgage, car title & registration",
  "Custody & divorce papers", "Health-insurance cards & a week of medications",
  "House & car keys (a spare set)", "Phone + charger (a prepaid one if yours is watched)",
  "A change of clothes", "A comfort item for each child",
  "Photos / evidence of abuse (on a drive they've never touched)",
];
const TECH = [
  "Plan on a safe device (library computer / a friend's phone / a new phone off the shared plan)",
  "Don't change passwords or remove trackers until an advocate helps you sequence it",
  "After you're out: new email → new passwords → 2-factor on your phone → remove their trusted devices",
  "Sweep the car, bag & coat for AirTags / Bluetooth tags",
  "Turn off location sharing; check Find My / Google / 'family locator' apps",
];

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #safety-plan-print, #safety-plan-print * { visibility: visible !important; }
  #safety-plan-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border: none !important; }
  #safety-plan-print .no-print { display: none !important; }
  #safety-plan-print input, #safety-plan-print textarea { border: 1px solid #999 !important; }
}`;

export const SafetyPlanBlock = ({ block }) => {
  const cfg = block.config || {};
  const accent = cfg.accent || "#9D174D";
  const [data, setData] = useState({});
  const [open, setOpen] = useState(Boolean(cfg.open_by_default));

  useEffect(() => {
    const warn = (e) => { if (Object.keys(data).length) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [data]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggle = (k) => setData((d) => ({ ...d, [k]: !d[k] }));
  const quickExit = () => { setData({}); window.location.replace("https://www.google.com"); };
  const clearAll = () => { if (window.confirm("Erase everything you've typed here?")) setData({}); };

  const Field = ({ k, label, ph, area }) => area ? (
    <label className="block">
      <span className="text-sm font-bold text-[#1A1A1A]">{label}</span>
      <textarea rows={2} value={data[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} autoComplete="off"
        className="mt-1 w-full rounded-lg border-2 px-3 py-2 text-[15px] focus:outline-none" style={{ borderColor: `${accent}40` }}
        data-testid={`plan-input-${k}`} />
    </label>
  ) : (
    <label className="block">
      <span className="text-sm font-bold text-[#1A1A1A]">{label}</span>
      <input value={data[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} autoComplete="off"
        className="mt-1 w-full rounded-lg border-2 px-3 py-2 text-[15px] focus:outline-none" style={{ borderColor: `${accent}40` }}
        data-testid={`plan-input-${k}`} />
    </label>
  );

  const Check = ({ k, label }) => (
    <li>
      <label className="flex items-start gap-2.5 cursor-pointer group">
        <input type="checkbox" checked={Boolean(data[k])} onChange={() => toggle(k)}
          className="mt-1 h-4 w-4 shrink-0 accent-current" style={{ color: accent }} data-testid={`plan-check-${k}`} />
        <span className={`text-[15px] leading-relaxed ${data[k] ? "line-through text-[#9a9a9a]" : "text-[#3a3a3a]"}`}>{label}</span>
      </label>
    </li>
  );

  const H = ({ children }) => <h4 className="text-sm md:text-base font-extrabold uppercase tracking-[0.13em] mt-6 first:mt-0" style={{ color: accent }}>{children}</h4>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-12 py-2" data-testid="safety-plan-block">
      <style>{PRINT_CSS}</style>
      <section id={cfg.anchor || "safety-plan"} className="scroll-mt-24 rounded-2xl border-2 bg-white overflow-hidden"
        style={{ borderColor: `${accent}4d`, boxShadow: `0 18px 50px -24px ${accent}73` }}>
        <div id="safety-plan-print" className="px-5 sm:px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 font-extrabold uppercase tracking-[0.15em] text-sm md:text-base" style={{ color: accent }}>
              <ShieldCheck className="h-5 w-5" /> {cfg.title || block.title || "Build your safety & exit plan"}
            </div>
            <button type="button" onClick={quickExit} data-testid="safety-plan-quick-exit"
              className="no-print inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A] text-white px-4 py-2 text-xs font-extrabold hover:opacity-90">
              <LogOut className="h-3.5 w-3.5" /> Quick exit
            </button>
          </div>
          <h2 className="mt-3 font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1A1A1A]">A plan is how you leave safely — and never go back alone.</h2>
          <div className="no-print mt-2 flex items-start gap-2 text-[13px] text-[#5C5C5C]">
            <Lock className="h-4 w-4 shrink-0 mt-0.5" style={{ color: accent }} />
            <p data-testid="safety-plan-privacy"><strong>Print-only. Nothing you type is saved — not on our site, not on this device.</strong> It disappears the moment you close or leave this page, so there is nothing for anyone to find later. Fill it out, hit <strong>Print</strong>, and keep the paper copy somewhere they'll never look. Use <strong>Quick exit</strong> to leave fast. Based on the <a href="https://www.thehotline.org/plan-for-safety/create-your-personal-safety-plan/" target="_blank" rel="noopener noreferrer" className="underline font-semibold" style={{ color: accent }}>National DV Hotline</a> plan.</p>
          </div>

          {!open ? (
            <button type="button" onClick={() => setOpen(true)} data-testid="safety-plan-open"
              className="no-print mt-5 inline-flex items-center gap-2 rounded-full bg-white border-2 px-6 py-3 text-base font-extrabold" style={{ color: accent, borderColor: `${accent}55` }}>
              Start my plan <ChevronDown className="h-5 w-5" />
            </button>
          ) : (
            <div className="mt-6 space-y-1">
              <H>People &amp; a code word</H>
              <p className="text-[14px] text-[#5C5C5C] mb-3">Pick people <em>outside</em> the group — not a mutual friend. A code word lets you signal "call for help / come get me" without saying it aloud.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field k="p1_name" label="Trusted person 1" ph="Name" />
                <Field k="p1_phone" label="Their number" ph="Phone" />
                <Field k="p2_name" label="Trusted person 2" ph="Name" />
                <Field k="p2_phone" label="Their number" ph="Phone" />
                <Field k="codeword" label="Our code word" ph="e.g. 'blue umbrella'" />
                <Field k="codeword_means" label="It means…" ph="e.g. call 911 / come get me now" />
              </div>

              <H>Where I'll go</H>
              <Field k="safe_places" label="Safe places, day or night" ph="A friend's, a shelter, a 24/7 place — addresses or names" area />

              <H>Go-bag — pack it, hide it (or leave it with a trusted person)</H>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-2">
                {GOBAG.map((l, i) => <Check key={i} k={`gobag_${i}`} label={l} />)}
              </ul>

              <H>Tech safety — order matters</H>
              <p className="text-[14px] text-[#B91C1C] font-semibold mb-2">Abusers escalate when they lose access. Talk to an advocate before you change anything.</p>
              <ul className="space-y-2 mt-1">
                {TECH.map((l, i) => <Check key={i} k={`tech_${i}`} label={l} />)}
              </ul>

              <H>If it's happening right now</H>
              <ul className="list-disc pl-5 space-y-1.5 text-[15px] text-[#3a3a3a] mt-2">
                <li>Get to a room with an exit and a phone. Avoid the kitchen, the bathroom and anywhere with weapons.</li>
                <li>Teach your kids a plan: a safe room, and how &amp; when to call 911.</li>
                <li>If you can, get outside toward people and lights. You do not have to explain yourself to leave.</li>
              </ul>

              <H>Reach a real person, 24/7</H>
              <ul className="list-disc pl-5 space-y-1.5 text-[15px] text-[#3a3a3a] mt-2">
                <li><strong>National DV Hotline — 1-800-799-7233</strong> (text START to 88788)</li>
                <li><strong>Crystal Judson Family Justice Center, Tacoma — 253-798-4166</strong> — advocacy, protection orders, safety planning in one place</li>
                <li><strong>988</strong> for crisis · <strong>911</strong> in an emergency</li>
              </ul>

              <Field k="notes" label="My own notes" ph="Anything else that keeps you safe" area />

              <div className="no-print flex flex-wrap items-center gap-3 pt-5">
                <button type="button" onClick={() => window.print()} data-testid="safety-plan-print"
                  className="inline-flex items-center gap-2 rounded-full text-white px-5 py-3 text-sm font-extrabold" style={{ backgroundColor: accent }}>
                  <Printer className="h-4 w-4" /> Print my plan
                </button>
                <button type="button" onClick={clearAll} data-testid="safety-plan-clear"
                  className="inline-flex items-center gap-2 rounded-full bg-white border-2 px-5 py-3 text-sm font-extrabold text-[#5C5C5C] border-[#e2e0dc] hover:text-[#B91C1C]">
                  <Trash2 className="h-4 w-4" /> Erase this plan
                </button>
                <span className="text-[12px] text-[#8a8a8a]" data-testid="safety-plan-nosave-note">Not saved anywhere — gone when you leave this page.</span>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SafetyPlanBlock;
